// ProfilePage.jsx - Clean, focused profile management
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../utils/auth';
import api from '../utils/api';
import { 
  User, 
  Mail, 
  Building, 
  Globe, 
  Briefcase, 
  Calendar, 
  Save, 
  RefreshCw, 
  CheckCircle,
  AlertCircle,
  Camera,
  MapPin,
  Phone,
  Crown,
  Eye,
  Zap,
  Target
} from 'lucide-react';

const ProfilePage = () => {
  const { user, refreshUser, getCurrentTier, TIER_CONFIG } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [profileData, setProfileData] = useState({
    displayName: '',
    email: '',
    company: '',
    industry: '',
    website: '',
    role: '',
    location: '',
    phone: ''
  });

  const currentTier = getCurrentTier();
  const tierConfig = TIER_CONFIG[currentTier] || TIER_CONFIG.observer;

  // Load profile data
  const loadProfileData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/me');
      
      setProfileData({
        displayName: response.data.displayName || '',
        email: response.data.email || '',
        company: response.data.company || '',
        industry: response.data.industry || '',
        website: response.data.website || '',
        role: response.data.role || '',
        location: response.data.location || '',
        phone: response.data.phone || ''
      });
      
    } catch (error) {
      console.error('Error loading profile:', error);
      setMessage('Error loading profile data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfileData();
  }, [loadProfileData]);

  // Sync with auth context
  useEffect(() => {
    if (user) {
      setProfileData(prev => ({
        ...prev,
        displayName: user.displayName || prev.displayName,
        email: user.email || prev.email,
        company: user.company || prev.company,
        industry: user.industry || prev.industry,
        website: user.website || prev.website,
        role: user.role || prev.role
      }));
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    if (message) setMessage('');
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const response = await api.put('/users/me', {
        displayName: profileData.displayName,
        company: profileData.company,
        industry: profileData.industry,
        website: profileData.website,
        role: profileData.role,
        location: profileData.location,
        phone: profileData.phone
      });
      
      setMessage('Profile updated successfully!');
      
      if (refreshUser) {
        await refreshUser();
      }
      
      setTimeout(() => setMessage(''), 3000);
      
    } catch (error) {
      console.error('Error saving profile:', error);
      setMessage(error.response?.data?.detail || 'Error saving profile');
    } finally {
      setSaving(false);
    }
  };

  const handleManualRefresh = async () => {
    setMessage('Refreshing profile...');
    await loadProfileData();
    if (refreshUser) await refreshUser();
    setMessage('Profile refreshed!');
    setTimeout(() => setMessage(''), 2000);
  };

  // Profile avatar based on tier
  const getAvatarGradient = () => {
    switch (currentTier) {
      case 'enterprise': return 'from-purple-500 to-pink-600';
      case 'business': return 'from-green-500 to-emerald-600';
      case 'professional': return 'from-blue-500 to-cyan-600';
      default: return 'from-gray-500 to-slate-600';
    }
  };

  const getTierIcon = () => {
    switch (currentTier) {
      case 'enterprise': return <Crown className="w-4 h-4" />;
      case 'business': return <Target className="w-4 h-4" />;
      case 'professional': return <Zap className="w-4 h-4" />;
      default: return <Eye className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl md:text-5xl font-black">
                <span className="bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent">
                  Your Profile
                </span>
              </h1>
              <p className="text-xl text-slate-300 mt-2">
                Manage your personal information
              </p>
            </div>
            
            <button
              onClick={handleManualRefresh}
              disabled={loading}
              className="flex items-center gap-2 bg-slate-700/50 hover:bg-slate-600/50 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors backdrop-blur-sm border border-slate-600/30"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl border flex items-center gap-2 backdrop-blur-sm ${
            message.includes('Error') || message.includes('error')
              ? 'bg-red-900/20 border-red-500/30 text-red-400'
              : message.includes('successfully') || message.includes('refreshed')
              ? 'bg-green-900/20 border-green-500/30 text-green-400'
              : 'bg-blue-900/20 border-blue-500/30 text-blue-400'
          }`}>
            {message.includes('successfully') || message.includes('refreshed') ? (
              <CheckCircle className="w-5 h-5" />
            ) : message.includes('Error') || message.includes('error') ? (
              <AlertCircle className="w-5 h-5" />
            ) : (
              <RefreshCw className="w-5 h-5" />
            )}
            {message}
          </div>
        )}

        {/* Main Profile Card */}
        <div className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-2xl overflow-hidden">
          
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/50 p-8 border-b border-slate-700/50">
            <div className="flex flex-col md:flex-row items-center gap-6">
              
              {/* Avatar */}
              <div className="relative">
                <div className={`w-32 h-32 bg-gradient-to-r ${getAvatarGradient()} rounded-2xl flex items-center justify-center text-white text-4xl font-bold shadow-2xl`}>
                  {profileData.displayName ? profileData.displayName.charAt(0).toUpperCase() : 
                   profileData.email ? profileData.email.charAt(0).toUpperCase() : 'U'}
                </div>
                <button className="absolute bottom-0 right-0 w-10 h-10 bg-slate-700/80 hover:bg-slate-600/80 backdrop-blur-sm border-2 border-slate-800 rounded-xl flex items-center justify-center transition-colors">
                  <Camera className="w-5 h-5 text-white" />
                </button>
                
                {/* Tier Badge */}
                <div className={`absolute -top-2 -right-2 bg-gradient-to-r ${getAvatarGradient()} px-3 py-1 rounded-full flex items-center gap-1 shadow-lg border border-white/20`}>
                  {getTierIcon()}
                  <span className="text-white text-xs font-bold">{tierConfig.name.toUpperCase()}</span>
                </div>
              </div>
              
              {/* Basic Info Display */}
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-3xl font-bold text-white mb-2">
                  {profileData.displayName || 'Your Name'}
                </h2>
                <p className="text-slate-300 flex items-center justify-center md:justify-start gap-2 mb-2">
                  <Mail className="w-4 h-4" />
                  {profileData.email || 'your.email@example.com'}
                </p>
                {profileData.company && (
                  <p className="text-slate-400 flex items-center justify-center md:justify-start gap-2 mb-2">
                    <Building className="w-4 h-4" />
                    {profileData.company}
                  </p>
                )}
                {profileData.role && (
                  <p className="text-slate-400 flex items-center justify-center md:justify-start gap-2">
                    <Briefcase className="w-4 h-4" />
                    {profileData.role}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSaveProfile} className="p-8">
            
            {loading ? (
              /* Loading State */
              <div className="animate-pulse space-y-6">
                {[...Array(8)].map((_, i) => (
                  <div key={i}>
                    <div className="h-4 bg-slate-700 rounded w-1/4 mb-3"></div>
                    <div className="h-12 bg-slate-700 rounded"></div>
                  </div>
                ))}
              </div>
            ) : (
              /* Form Fields */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Display Name */}
                <div className="md:col-span-2">
                  <label className="block text-white font-medium mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={profileData.displayName}
                    onChange={(e) => handleInputChange('displayName', e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter your display name"
                  />
                </div>

                {/* Email (Read-only) */}
                <div className="md:col-span-2">
                  <label className="block text-white font-medium mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={profileData.email}
                    readOnly
                    className="w-full bg-slate-700/30 border border-slate-600/50 rounded-xl px-4 py-3 text-slate-300 cursor-not-allowed"
                  />
                  <p className="text-slate-400 text-sm mt-2">
                    Email cannot be changed here. Visit Settings to update your email.
                  </p>
                </div>

                {/* Company */}
                <div>
                  <label className="block text-white font-medium mb-3 flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Company
                  </label>
                  <input
                    type="text"
                    value={profileData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Your company name"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block text-white font-medium mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Job Title
                  </label>
                  <input
                    type="text"
                    value={profileData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Your job title or role"
                  />
                </div>

                {/* Industry */}
                <div>
                  <label className="block text-white font-medium mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Industry
                  </label>
                  <select
                    value={profileData.industry}
                    onChange={(e) => handleInputChange('industry', e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="">Select industry</option>
                    <option value="Technology">Technology</option>
                    <option value="E-commerce">E-commerce</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Finance">Finance</option>
                    <option value="Education">Education</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Consulting">Consulting</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Food & Beverage">Food & Beverage</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Website */}
                <div>
                  <label className="block text-white font-medium mb-3 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Website
                  </label>
                  <input
                    type="url"
                    value={profileData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="https://yourwebsite.com"
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-white font-medium mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Location
                  </label>
                  <input
                    type="text"
                    value={profileData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="City, Country"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-white font-medium mb-3 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="flex items-center justify-between mt-8 pt-8 border-t border-slate-700/50">
              <button
                type="submit"
                disabled={saving || loading}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-3 rounded-xl font-semibold transition-all shadow-lg"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
              
              <div className="text-slate-400 text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Member since: {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
              </div>
            </div>
          </form>
        </div>

        {/* Account Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 text-center">
            <div className={`w-12 h-12 bg-gradient-to-r ${getAvatarGradient()} rounded-xl flex items-center justify-center mx-auto mb-3`}>
              {getTierIcon()}
            </div>
            <div className="text-2xl font-bold text-white mb-1">
              {tierConfig.name}
            </div>
            <div className="text-slate-400 text-sm">Current Plan</div>
          </div>
          
          <div className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div className="text-2xl font-bold text-white mb-1">
              {user?.total_analyses || 0}
            </div>
            <div className="text-slate-400 text-sm">Total Analyses</div>
          </div>
          
          <div className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Eye className="w-6 h-6 text-white" />
            </div>
            <div className="text-2xl font-bold text-white mb-1">
              {user?.profile_views || 0}
            </div>
            <div className="text-slate-400 text-sm">Profile Views</div>
          </div>
          
          <div className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div className="text-2xl font-bold text-white mb-1">
              {user?.last_login_at ? Math.floor((Date.now() - new Date(user.last_login_at)) / (1000 * 60 * 60 * 24)) : 0}
            </div>
            <div className="text-slate-400 text-sm">Days Active</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;